import type { PaigeClient } from '@paige/api-client';
import type {
  CustomerFilterSet,
  CustomerList,
  CustomerSort,
  CustomerSummary,
  PickerPartner,
  SortDir,
} from '@paige/admin';
import { unwrap } from './http';

/** What the Customers page's numbers are asked for: a period and what narrows the companies. */
export interface CustomerQuery {
  /** The period's first and last UTC day, both counted. */
  from: string;
  until: string;
  filters: CustomerFilterSet;
}

/** A page of the list: the same question, an order and where the page starts. */
export interface CustomerListQuery extends CustomerQuery {
  sort: CustomerSort;
  dir: SortDir;
  offset: number;
  limit: number;
}

/** The query the three reads share: the period and only the filters that are on. */
export function customerParams(q: CustomerQuery) {
  const f = q.filters;
  return {
    from: q.from,
    until: q.until,
    ...(f.q ? { q: f.q } : {}),
    ...(f.plan ? { plan: f.plan } : {}),
    ...(f.paying ? { paying: true } : {}),
    ...(f.pastDue ? { pastDue: true } : {}),
    ...(f.test ? { test: true } : {}),
    ...(f.problems ? { problems: true } : {}),
    ...(f.view !== 'all' ? { view: f.view } : {}),
    ...(f.partner ? { partner: f.partner } : {}),
  };
}

/**
 * The Customers page's reads (`/admin/customers`, its summary, `/admin/partners`): the ledger searches,
 * filters, sorts and adds up, and answers a page at a time. Nothing here asks for every company.
 */
export function customersApi(client: PaigeClient) {
  return {
    async listCustomers(q: CustomerListQuery): Promise<CustomerList> {
      const query = {
        ...customerParams(q),
        sort: q.sort,
        dir: q.dir,
        offset: q.offset,
        limit: q.limit,
      };
      return unwrap(await client.GET('/admin/customers', { params: { query } })) as CustomerList;
    },
    async summariseCustomers(q: CustomerQuery): Promise<CustomerSummary> {
      const r = await client.GET('/admin/customers/summary', {
        params: { query: customerParams(q) },
      });
      return unwrap(r) as CustomerSummary;
    },
    /** The partner picker's search: codes that start with what is typed, twenty at most. */
    async findPartners(q: string): Promise<PickerPartner[]> {
      const r = await client.GET('/admin/partners', { params: { query: q ? { q } : {} } });
      return (unwrap(r) as { partners: PickerPartner[] }).partners;
    },
  };
}

export type CustomersApi = ReturnType<typeof customersApi>;

/**
 * Where the list downloads as CSV (`GET /v1/admin/customers/export`): the same period, filters and order,
 * every row the filters leave (5,000 at most). A plain link: the session cookie goes with it on the app's
 * own origin, so the switch for test companies is written into it here.
 */
export function customersExportUrl(
  q: CustomerQuery & { sort: CustomerSort; dir: SortDir },
  hideTests: boolean,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries({ ...customerParams(q), sort: q.sort, dir: q.dir }))
    params.set(key, String(value));
  if (hideTests) params.set('tests', 'hide');
  return `/v1/admin/customers/export?${params.toString()}`;
}
